<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const form = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const updateField = (key, value) => {
  if (props.readonly) return;
  emit('update:modelValue', { ...props.modelValue, [key]: value });
};

const onRecurringChange = value => {
  if (props.readonly) return;
  const next = { ...props.modelValue, is_recurring: value };
  if (value) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(next.startDate || '')) {
      next.startDate = next.startDate.slice(-5);
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(next.endDate || '')) {
      next.endDate = next.endDate.slice(-5);
    }
  }
  emit('update:modelValue', next);
};
</script>

<template>
  <div class="space-y-3">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
      <div v-if="showTitle" class="min-w-0 flex-1">
        <Input
          :model-value="form.title"
          size="sm"
          :label="t('SETTINGS.SPACES.SCHEDULE.CLOSED_PERIOD_TITLE')"
          :placeholder="
            t('SETTINGS.SPACES.SCHEDULE.CLOSED_PERIOD_TITLE_PLACEHOLDER')
          "
          :disabled="readonly"
          @update:model-value="value => updateField('title', value)"
        />
      </div>
      <div
        class="flex shrink-0 items-center gap-2"
        :class="showTitle ? 'pb-2' : ''"
      >
        <Switch
          class="shrink-0"
          :class="{ 'pointer-events-none opacity-60': readonly }"
          :model-value="form.is_recurring"
          @update:model-value="onRecurringChange"
        />
        <span class="text-xs whitespace-nowrap text-n-slate-11">
          {{ t('SETTINGS.SPACES.SCHEDULE.RECURRING') }}
        </span>
      </div>
    </div>

    <div
      class="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-[1fr_7rem_1fr_7rem]"
    >
      <Input
        :model-value="form.startDate"
        size="sm"
        :type="form.is_recurring ? 'text' : 'date'"
        :label="t('SETTINGS.SPACES.SCHEDULE.START')"
        :placeholder="form.is_recurring ? 'MM-DD' : ''"
        :disabled="readonly"
        @update:model-value="value => updateField('startDate', value)"
      />
      <Input
        :model-value="form.startTime"
        size="sm"
        type="time"
        :label="t('SETTINGS.SPACES.SCHEDULE.START_TIME')"
        :disabled="readonly"
        @update:model-value="value => updateField('startTime', value)"
      />
      <Input
        :model-value="form.endDate"
        size="sm"
        :type="form.is_recurring ? 'text' : 'date'"
        :label="t('SETTINGS.SPACES.SCHEDULE.END')"
        :placeholder="form.is_recurring ? 'MM-DD' : ''"
        :disabled="readonly"
        @update:model-value="value => updateField('endDate', value)"
      />
      <Input
        :model-value="form.endTime"
        size="sm"
        type="time"
        :label="t('SETTINGS.SPACES.SCHEDULE.END_TIME')"
        :disabled="readonly"
        @update:model-value="value => updateField('endTime', value)"
      />
    </div>

    <p v-if="!readonly" class="text-xs text-n-slate-10">
      {{ t('SETTINGS.SPACES.SCHEDULE.CLOSED_PERIOD_TIME_HINT') }}
    </p>
  </div>
</template>
